import { useState } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";

import {
  Container,
  PageTitleWrapper,
  InfoText,
  DownloadLinkWrapper,
  TableResult,
} from "~/pages/BearingFrequencyCalculatorPage/BearingFrequencyCalculatorPage.styles";
import { type BearingFrequencyCalculationResult } from "~/pages/BearingFrequencyCalculatorPage/utils/calculateBearingFrequency";
import { downloadBearingFrequencyPdf } from "~/pages/BearingFrequencyCalculatorPage/api/downloadBearingFrequencyPdf";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { DownloadClickLink } from "~/common/components/Link";
import { FormBFC } from "~/pages/BearingFrequencyCalculatorPage/components/FormBFC";
import { TableBFC } from "~/pages/BearingFrequencyCalculatorPage/components/TableBFC";
import { trackBearing } from "~/app/analytics/ga/ga";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";

export interface BearingFrequencyCalculatorForm {
  isInch: boolean;
  pitchDiameter: number | null;
  rollingDiameter: number | null;
  isDeg: boolean;
  contactAngle: number | null;
  rollingElementAmount: number | null;
  speed: number | null;
}

const RESULT_NF = NumberFormatter.getFormatter({
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const INPUT_NF = NumberFormatter.getFormatter();

interface BearingFrequencyCalculatorProps {
  initialResult?: BearingFrequencyCalculationResult | null;
}

export function BearingFrequencyCalculator({ initialResult = null }: BearingFrequencyCalculatorProps) {
  const { t } = useTranslation();
  const [result, setResult] = useState<BearingFrequencyCalculationResult | null>(initialResult);

  function handleChange(newValue: BearingFrequencyCalculationResult | null) {
    setResult(newValue);
  }

  return (
    <Container>
      <PageTitleWrapper>
        <PageTitle title={t("bearingFrequencyCalculator.title")} />
      </PageTitleWrapper>

      <FormBFC handleChange={handleChange} />

      <InfoText
        dangerouslySetInnerHTML={{ __html: t("bearingFrequencyCalculator.infoText") }}
      />

      <DownloadPDF result={result} />
      <TableResult>
        <TableBFC result={result} />
      </TableResult>
    </Container>
  );
}

interface DownloadProps {
  result: BearingFrequencyCalculationResult | null;
}

const DownloadPDF = ({ result }: DownloadProps) => {
  const isValidResult = !!result;

  const downloadPdf = () => {
    if (!result) {
      return;
    }
    const {
      cor,
      or,
      re,
      cir,
      ir,
      isInch,
      isDeg,
      rollingDiameter,
      pitchDiameter,
      rollingElementAmount,
      speed,
      contactAngle,
    } = result;
    trackBearing("click_on_download_results_table");
    downloadBearingFrequencyPdf({
      isInch,
      isDeg,
      rollingDiameter: INPUT_NF.format(rollingDiameter),
      pitchDiameter: INPUT_NF.format(pitchDiameter),
      rollingElementAmount: INPUT_NF.format(rollingElementAmount),
      speed: INPUT_NF.format(speed),
      contactAngle: INPUT_NF.format(contactAngle),
      cor: RESULT_NF.format(cor),
      or: RESULT_NF.format(or),
      re: RESULT_NF.format(re),
      cir: RESULT_NF.format(cir),
      ir: RESULT_NF.format(ir),
    });
  };

  const { t } = useTranslation();
  return (
    <DownloadLinkWrapper>
      <DownloadTitle>{t("page.bearingFrequencyCalculator.download")}</DownloadTitle>
      <DownloadClickLink onClick={downloadPdf} disabled={!isValidResult}>
        {t("downloadFile.type.pdf")}
      </DownloadClickLink>
    </DownloadLinkWrapper>
  );
};

const DownloadTitle = styled("span")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(2),
}));
